import { t } from '../i18n';
import { NavLink, Link } from 'react-router-dom';
import { X } from 'lucide-react';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const navLinks = [
    { to: '/', label: t("Home") },
    { to: '/about', label: t("About") },
    { to: '/portfolio', label: t("Work") },
    { to: '/blog', label: 'Blog' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-background/95 backdrop-blur-md z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 z-50 transform transition-transform duration-500 ease-out-expo ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        {/* Background with subtle gradient */}
        <div className="absolute inset-0 bg-background">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-transparent to-accent/[0.02]" />
          {/* Right border accent */}
          <div className="absolute top-0 right-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary/20 to-transparent" />
        </div>

        <div className="relative flex flex-col h-full px-8 py-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-16">
            <Link
              to="/"
              onClick={onClose}
              className="flex items-center gap-3 group"
            >
              <div className="relative">
                <Logo />
                {/* Glow effect on hover */}
                <div className="absolute inset-0 bg-primary/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-heading font-semibold text-foreground tracking-wide">
                  Virtuoz
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted">
                  Solutions
                </span>
              </div>
            </Link>
            <button
              onClick={onClose}
              className="lg:hidden p-2 text-muted hover:text-primary transition-colors duration-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1">
            <ul className="space-y-2">
              {navLinks.map(({ to, label }, index) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `group relative flex items-center py-3 text-sm font-heading font-medium tracking-wide transition-all duration-300 ${
                        isActive
                          ? 'text-primary'
                          : 'text-foreground/50 hover:text-foreground'
                      }`
                    }
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {({ isActive }) => (
                      <>
                        {/* Active indicator */}
                        <span
                          className={`absolute left-0 w-0.5 h-full bg-primary transition-all duration-300 ${
                            isActive ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                        {/* Hover line effect */}
                        <span
                          className={`absolute left-0 w-0 h-full bg-primary/10 transition-all duration-300 group-hover:w-full ${
                            isActive ? 'w-full' : ''
                          }`}
                        />
                        <span className="relative pl-4">{label}</span>
                        {/* Arrow on hover */}
                        <span className={`ml-auto text-primary opacity-0 transform translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 ${isActive ? 'opacity-100 translate-x-0' : ''}`}>
                          &rarr;
                        </span>
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Footer */}
          <div className="space-y-8">
            {/* Social Links */}
            <div className="flex gap-6">
              <a
                href="https://www.linkedin.com/in/enzo-chevallier-6a69b77b/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs uppercase tracking-widest text-muted hover:text-primary transition-colors duration-300 link-hover"
              >
                LinkedIn
              </a>
              <a
                href="https://x.com/EnzoChevallier"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs uppercase tracking-widest text-muted hover:text-primary transition-colors duration-300 link-hover"
              >
                X
              </a>
              <a
                href="mailto:contact@virtuoz-solutions.com"
                className="text-xs uppercase tracking-widest text-muted hover:text-primary transition-colors duration-300 link-hover"
              >
                Email
              </a>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-gradient-to-r from-primary/20 via-primary/10 to-transparent" />

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Copyright */}
            <p className="text-[10px] text-muted/50 tracking-wide">
              &copy; {new Date().getFullYear()} Virtuoz Solutions
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
